// event type in order to (listen to) or (fire) // Here we can use any type for payload
// eslint-disable-next-line @typescript-eslint/no-explicit-any

import { Subject } from 'rxjs';

export type TUseEventBusKey = string | number | symbol;

export type TUseEventBusHandler<PayloadType> = (data: {
  id?: string | symbol;
  payload: PayloadType;
}) => void;

export type TUseEventbusProps<
  KeyType extends TUseEventBusKey,
  PayloadType = any
> = Record<KeyType, TUseEventBusHandler<PayloadType>>;

export type EventbusEvent<PayloadType = any> = {
  // naming convention for key is: [scope].[action]
  // ex. CasinoEvent.CasinoGamesRendered
  key: keyof TUseEventbusProps<
    string | number | symbol,
    TUseEventBusHandler<PayloadType>
  >;
  id?: string | symbol;
  payload?: PayloadType;
};

// event bus Subject Observable

type TCleanEventBus = () => void;
type TFireEvent = (event: EventbusEvent) => void;
type TFireEvents = (events: EventbusEvent[]) => void;
type TInitEventBus = <KeyType extends TUseEventBusKey, PayloadType>(
  events?: TUseEventbusProps<KeyType, PayloadType>
) => TCleanEventBus | void;

export enum EBottomNavEvent {
  OPERATOR_MOUNTED = 'bottom-nav-operator-mounted',
  UPDATE_COUNT = 'bottom-nav-update-count',
  FETCH_COUNT = 'bottom-nav-fetch-count',
  ITEM_CLICK = 'bottom-nav-item-click',
  CHECK_OPEN_BETS = 'bottom-nav-check-open-bets',
  UPDATE_OPEN_BETS_ELIGIBLE = 'bottom-nav-update-open-bets-eligible'
}

export const LOADING_PATH_CHANGE_TRIGGER_PREFIX = '__path__';

export enum EExternalUpdaterEvent {
  LOGGED_IN = 'external-logged-in',
  LOGGED_OUT = 'external-logged-out',
  OPEN_POPUP = 'external-open-popup',
  CLOSE_POPUP = 'external-close-popup',
  ADD_TRIGGERING_RULES = 'external-add-triggering-rules',
  LOADING_STARTED = 'loadingStarted',
  LOADING_ENDS_FOR_PAGE = 'loadingEndsForPage'
}

export enum ELanguageSwitcherChangeEvents {
  HANDLE_SITE_LANGUAGE_CHANGE = 'handle-site-language-change',
  SITE_LANGUAGE_CHANGED = 'external-site-language-changed'
}
export enum EGlobalMemoryEvent {
  CURRENT_PAGE_DATA = 'current-page-data'
}

const createEventBusInitializer = (
  subject: Subject<EventbusEvent>
): [
  initEventBus: TInitEventBus,
  fireEvet: TFireEvent,
  fireEvents: TFireEvents
] => {
  const fireEvent: TFireEvent = event => {
    subject.next(event);
  };

  const fireEvents: TFireEvents = events => {
    events.forEach(event => {
      subject.next(event);
    });
  };

  const initEventBus: TInitEventBus = events => {
    if (!events || !Object.values(events).length) {
      return;
    }

    // subscribe to the Subject Observable
    const subscription = subject.subscribe(({ key, id, payload }) => {
      if (key in events) {
        events[key as keyof typeof events]?.({ id, payload });
      }
    });

    // we should unsubscribe after component's unmount to remove extra subscribes
    return () => {
      subscription.unsubscribe();
    };
  };

  return [initEventBus, fireEvent, fireEvents];
};

export const eventbusSubject = new Subject<EventbusEvent>();

const [initEventBus, fireEvent, fireEvents] =
  createEventBusInitializer(eventbusSubject);

export { initEventBus, fireEvent, fireEvents, createEventBusInitializer };
